/**
 * Formatting utility functions for the Apnea Coach Manager
 * All times displayed in LOCAL timezone with 24-hour format
 */

/**
 * Format seconds into MM:SS display format
 * @param {number} seconds - Total seconds
 * @returns {string} Formatted time string (MM:SS)
 */
export const formatTimeDisplay = (seconds) => {
  if (seconds === null || seconds === undefined) return '00:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

/**
 * Format seconds into countdown display (handles hours if needed)
 * @param {number} seconds - Total seconds
 * @returns {string} Formatted countdown string
 */
export const formatCountdown = (seconds) => {
  if (seconds === null || seconds === undefined || seconds <= 0) return '00:00';
  
  const hours = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  
  if (hours > 0) {
    return `${hours}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

/**
 * Format ISO datetime string to local time display (HH:MM) - 24-hour format
 * @param {string} dateTime - ISO datetime string
 * @returns {string} Formatted local time (HH:MM) in 24-hour format
 */
export const formatDateTime = (dateTime) => {
  if (!dateTime) return 'N/A';
  try {
    const date = new Date(dateTime);
    return date.toLocaleTimeString('en-GB', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: false 
    });
  } catch {
    return 'N/A';
  }
};

/**
 * Format ISO datetime string to local time with seconds (HH:MM:SS) - 24-hour format
 * @param {string} dateTime - ISO datetime string
 * @returns {string} Formatted local time (HH:MM:SS) in 24-hour format
 */
export const formatDateTimeWithSeconds = (dateTime) => {
  if (!dateTime) return 'N/A';
  try {
    const date = new Date(dateTime);
    return date.toLocaleTimeString('en-GB', { 
      hour: '2-digit', 
      minute: '2-digit',
      second: '2-digit',
      hour12: false 
    });
  } catch {
    return 'N/A';
  }
};

/**
 * Format date for display in "Full day DD Month YYYY" format
 * Supports both English and French locales
 * @param {string} dateString - Date string (ISO format or any parseable date)
 * @param {string} language - Language code ('en' or 'fr'), defaults to 'en'
 * @returns {string} Formatted date (e.g., "Monday 15 January 2026" or "Lundi 15 janvier 2026")
 */
export const formatDate = (dateString, language = 'en') => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    
    const locale = language === 'fr' ? 'fr-FR' : 'en-GB';
    
    return date.toLocaleDateString(locale, {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  } catch {
    return 'N/A';
  }
};

/**
 * Format date in short format (DD/MM/YYYY)
 * @param {string} dateString - Date string
 * @param {string} language - Language code ('en' or 'fr')
 * @returns {string} Formatted date
 */
export const formatDateShort = (dateString, language = 'en') => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    
    const locale = language === 'fr' ? 'fr-FR' : 'en-GB';
    
    return date.toLocaleDateString(locale, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  } catch {
    return 'N/A';
  }
};

/**
 * Format full datetime for display - 24-hour format with full date
 * @param {string} dateTime - ISO datetime string
 * @param {string} language - Language code ('en' or 'fr')
 * @returns {string} Formatted date and time (e.g., "Monday 15 January 2026 14:30")
 */
export const formatFullDateTime = (dateTime, language = 'en') => {
  if (!dateTime) return 'N/A';
  try {
    const date = new Date(dateTime);
    if (isNaN(date.getTime())) return 'N/A';
    
    const locale = language === 'fr' ? 'fr-FR' : 'en-GB';
    
    const datePart = date.toLocaleDateString(locale, {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
    
    const timePart = date.toLocaleTimeString('en-GB', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: false 
    });
    
    return `${datePart} ${timePart}`;
  } catch {
    return 'N/A';
  }
};

/**
 * Get current local time in HH:MM:SS format (24-hour)
 * @returns {string} Current local time
 */
export const getCurrentTimeString = () => {
  return new Date().toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });
};

/**
 * Format duration in seconds to MM:SS
 * @param {number} seconds - Duration in seconds
 * @returns {string} Formatted duration
 */
export const formatDuration = (seconds) => {
  if (seconds === null || seconds === undefined || seconds < 0) return '00:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};
